import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { useCallback, useState } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { WSQueries } from "../../../query/WSQuery";
import { selectorActiveBankingAccount } from "../../../shared/selectors/selectorActiveBankingAccount";
import { ConfirmOrderPhysicalCard } from "../../Wallet/components/ConfirmOrderPhysicalCard";
import { FormCardAcknowledgements } from "../../Wallet/components/FormCardAcknowledgements";
import { useCardOrderedModal } from "../../Wallet/modals/OrderConfirmation/useCardOrderedModal";
import { WalletCardType } from "../../Wallet/types";
import { EmbeddedLayout } from "../components/EmbeddedLayout";
import { EmbeddedOrderVirtualCard } from "../components/WalletCards/EmbeddedOrderVirtualCard";
import { EmbeddedWalletCardDetails } from "../components/WalletCards/EmbeddedWalletCardDetails";
import {
  EmbeddedWalletCardsLanding,
  WalletCardsSkeleton
} from "../components/WalletCards/EmbeddedWalletCardsLanding";
import { EmbeddedWalletNotFound } from "../components/WalletCards/EmbeddedWalletNotFound";
import { sendCardOrderedToParent } from "../components/WalletCards/walletCardEvents";
import { useNotifyParentOnSessionExpired } from "../hooks/useNotifyParentOnSessionExpired";
import {
  PATH_EMBEDDED_MANAGE_WALLET_CARDS,
  PATH_EMBEDDED_MANAGE_WALLET_CARDS_CONFIRM_PHYSICAL,
  PATH_EMBEDDED_MANAGE_WALLET_CARDS_ORDER_PHYSICAL,
  PATH_EMBEDDED_MANAGE_WALLET_CARDS_ORDER_VIRTUAL,
  routeHelpersEmbeddedManageWalletCardsCard
} from "../paths";
import { parseAllowedCardTypes } from "../utils/parseAllowedCardTypes";

const ORDER_PATHS: Record<WalletCardType, string> = {
  virtual: PATH_EMBEDDED_MANAGE_WALLET_CARDS_ORDER_VIRTUAL,
  physical: PATH_EMBEDDED_MANAGE_WALLET_CARDS_ORDER_PHYSICAL
};

export const RouteManageWalletCards: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();
  const cardOrderedModal = useCardOrderedModal();
  const queryInternalAccounts = useQueryInternalAccounts();

  useNotifyParentOnSessionExpired();

  const [allowedCardTypes] = useState(() =>
    parseAllowedCardTypes(
      new URLSearchParams(location.search).get("allowedCardTypes") || ""
    )
  );

  const goTo = useCallback(
    (pathname: string) => history.push({ pathname, search: location.search }),
    [history, location.search]
  );
  const goToLanding = useCallback(
    () => goTo(PATH_EMBEDDED_MANAGE_WALLET_CARDS),
    [goTo]
  );

  return (
    <EmbeddedLayout>
      <WSQueries
        queries={{ queryInternalAccounts }}
        renderLoader={() => (
          <WalletCardsSkeleton allowedCardTypes={allowedCardTypes} />
        )}
      >
        {({ queryInternalAccountsData }) =>
          queryInternalAccountsData.some(selectorActiveBankingAccount) ? (
            <Switch>
              <Route path={PATH_EMBEDDED_MANAGE_WALLET_CARDS_ORDER_VIRTUAL}>
                <EmbeddedOrderVirtualCard
                  onBack={goToLanding}
                  onCardExists={() =>
                    history.replace({
                      pathname: PATH_EMBEDDED_MANAGE_WALLET_CARDS,
                      search: location.search
                    })
                  }
                  onOrdered={card => {
                    openSnackbar({
                      message: "Virtual card is ready to use",
                      type: "success"
                    });
                    sendCardOrderedToParent(card);
                    goToLanding();
                  }}
                />
              </Route>

              <Route path={PATH_EMBEDDED_MANAGE_WALLET_CARDS_CONFIRM_PHYSICAL}>
                <ConfirmOrderPhysicalCard
                  onBack={goToLanding}
                  onOrdered={async card => {
                    sendCardOrderedToParent(card);
                    await cardOrderedModal.open();
                    goToLanding();
                  }}
                />
              </Route>

              <Route path={PATH_EMBEDDED_MANAGE_WALLET_CARDS_ORDER_PHYSICAL}>
                <FormCardAcknowledgements
                  cardType={WalletCardType.Physical}
                  onBack={goToLanding}
                  onContinue={() =>
                    goTo(PATH_EMBEDDED_MANAGE_WALLET_CARDS_CONFIRM_PHYSICAL)
                  }
                />
              </Route>

              <Route path={routeHelpersEmbeddedManageWalletCardsCard.path}>
                <EmbeddedWalletCardDetails onBack={goToLanding} />
              </Route>

              <Route path={PATH_EMBEDDED_MANAGE_WALLET_CARDS}>
                <EmbeddedWalletCardsLanding
                  allowedCardTypes={allowedCardTypes}
                  onOrder={cardType => goTo(ORDER_PATHS[cardType])}
                  onManage={cardId =>
                    goTo(
                      routeHelpersEmbeddedManageWalletCardsCard.createLocationObject(
                        { cardId }
                      ).pathname!
                    )
                  }
                />
              </Route>
            </Switch>
          ) : (
            <EmbeddedWalletNotFound />
          )
        }
      </WSQueries>
    </EmbeddedLayout>
  );
};
